import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "../components/ui/chart";
// import { DateRangePicker } from "../components/ui/daterangepicker";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
} from "recharts";
import { Cpu, HardDrive, Network, Activity, TriangleAlert } from "lucide-react";
import { useLazyGetUtilizationDashboardQuery } from "../store/api/dashboardApi";

export default function UtilizationDashboard({ instanceId }) {
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  const [metricsData, setMetricsData] = useState([]);
  const [fetchDashboard, { data }] = useLazyGetUtilizationDashboardQuery();

  useEffect(() => {
    setMetricsData(data);
  }, [data]);

  useEffect(() => {
    //   if (!dateRange.from || !dateRange.to) return;
    fetchDashboard();
    //   async function load() {
    //     const response = await fetchDashboard();
    //     console.log(response);
    //     setMetricsData(response);
    //   }

    //   load();
  }, [dateRange, instanceId]);

  // Helpers
  const getMetric = (name) =>
    metricsData?.find((m) => m.metric === name)?.datapoints || [];

  const COLORS = ["#3b82f6", "#10b981", "#facc15", "#ef4444"]; // AI style bright colors

  const failedInstance = getMetric("StatusCheckFailed_Instance").some(
    (x) => x.Average > 0
  );
  const failedSystem = getMetric("StatusCheckFailed_System").some(
    (x) => x.Average > 0
  );

  return (
    <div className="-m-4 p-4 bg-[#F8FAFF]">
      <div className="-m-2 mb-6 px-6 py-2 bg-rose-100 border border-rose-500 text-rose-500 rounded-md flex items-center space-x-2">
        <TriangleAlert className="w-5" />
        <div>Temporary utilization dashboard</div>
      </div>

      <div className="col-span-full flex justify-between items-center mb-6">
        <h1 className="text-md font-bold text-blue-600">
          Utilization Dashboard
        </h1>
        {/* <DateRangePicker value={dateRange} onChange={setDateRange} /> */}
      </div>

      <div className="grid gap-6 grid-cols-1 md:grid-cols-2 xl:grid-cols-4 auto-rows-max">
        {/* CPU UTILIZATION (Area Chart) */}
        <Card className="rounded-2xl shadow-md border border-blue-200 col-span-2 xl:col-span-1">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-blue-700 text-lg">
              <Cpu className="w-5 h-5 text-blue-500" /> CPU Utilization
            </CardTitle>
          </CardHeader>
          <CardContent className="px-0">
            <ChartContainer
              width="100%"
              height={150}
              className="px-0"
              config={{
                average: {
                  label: "Average",
                },
              }}
            >
              <AreaChart data={getMetric("CPUUtilization")}>
                <XAxis hide dataKey="Timestamp" />
                <YAxis hide />
                <CustomChartTooltip />
                <Area
                  type="monotone"
                  dataKey="Average"
                  stroke="#3b82f6"
                  fill="#bfdbfe"
                />
              </AreaChart>
            </ChartContainer>
          </CardContent>
        </Card>

        {/* NETWORK IN (Bar) */}
        <Card className="rounded-2xl shadow-md border border-green-200 xl:col-span-1">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-green-700 text-lg">
              <Network className="w-5 h-5 text-green-500" /> Network In
            </CardTitle>
          </CardHeader>
          <CardContent className="px-0">
            <ChartContainer
              width="100%"
              height={150}
              config={{
                average: {
                  label: "Average",
                },
              }}
            >
              <BarChart data={getMetric("NetworkIn")}>
                <XAxis hide dataKey="Timestamp" />
                <YAxis hide />
                <CustomChartTooltip />
                <Bar dataKey="Average" fill="#10b981" />
              </BarChart>
            </ChartContainer>
          </CardContent>
        </Card>

        {/* NETWORK OUT (Bar) */}
        <Card className="rounded-2xl shadow-md border border-green-200 xl:col-span-1">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-green-700 text-lg">
              <Network className="w-5 h-5 text-green-500" /> Network Out
            </CardTitle>
          </CardHeader>
          <CardContent className="px-0">
            <ChartContainer
              width="100%"
              height={150}
              config={{
                average: {
                  label: "Average",
                },
              }}
            >
              <BarChart data={getMetric("NetworkOut")}>
                <XAxis hide dataKey="Timestamp" />
                <YAxis hide />
                <CustomChartTooltip />
                <Bar dataKey="Average" fill="#34d399" />
              </BarChart>
            </ChartContainer>
          </CardContent>
        </Card>

        {/* DISK READ (Line) */}
        <Card className="rounded-2xl shadow-md border border-yellow-200 xl:col-span-1">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-yellow-700 text-lg">
              <HardDrive className="w-5 h-5 text-yellow-500" /> Disk Read
            </CardTitle>
          </CardHeader>
          <CardContent className="px-0">
            <ChartContainer
              width="100%"
              height={150}
              config={{
                average: {
                  label: "Average",
                },
              }}
            >
              <LineChart data={getMetric("DiskReadBytes")}>
                <XAxis hide />
                <YAxis hide />
                <Tooltip />
                <Line
                  dataKey="Average"
                  dot={false}
                  stroke="#facc15"
                  strokeWidth={3}
                />
              </LineChart>
            </ChartContainer>
          </CardContent>
        </Card>

        {/* DISK WRITE (Line) */}
        <Card className="rounded-2xl shadow-md border border-yellow-200 xl:col-span-1">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-yellow-700 text-lg">
              <HardDrive className="w-5 h-5 text-yellow-500" /> Disk Write
            </CardTitle>
          </CardHeader>
          <CardContent className="px-0">
            <ChartContainer
              width="100%"
              height={150}
              config={{
                average: {
                  label: "Average",
                },
              }}
            >
              <LineChart data={getMetric("DiskWriteBytes")}>
                <XAxis hide />
                <YAxis hide />
                <Tooltip />
                <Line
                  dataKey="Average"
                  dot={false}
                  stroke="#f59e0b"
                  strokeWidth={3}
                />
              </LineChart>
            </ChartContainer>
          </CardContent>
        </Card>

        {/* STATUS CHECK INSTANCE */}
        <Card
          className={`rounded-2xl shadow-md xl:col-span-1 ${
            failedInstance
              ? "border-red-300 bg-red-50"
              : "border-green-300 bg-green-50"
          }`}
        >
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Activity className="w-5 h-5" /> Instance Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p
              className={`text-xl font-semibold ${
                failedInstance ? "text-red-600" : "text-green-700"
              }`}
            >
              {failedInstance ? "FAILED" : "OK"}
            </p>
          </CardContent>
        </Card>

        {/* STATUS CHECK SYSTEM */}
        <Card
          className={`rounded-2xl shadow-md xl:col-span-1 ${
            failedSystem
              ? "border-red-300 bg-red-50"
              : "border-green-300 bg-green-50"
          }`}
        >
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Activity className="w-5 h-5" /> System Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p
              className={`text-xl font-semibold ${
                failedSystem ? "text-red-600" : "text-green-700"
              }`}
            >
              {failedSystem ? "FAILED" : "OK"}
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function CustomChartTooltip() {
  return (
    <ChartTooltip
      cursor={false}
      content={
        <ChartTooltipContent
          labelFormatter={(value) => {
            return new Date(value).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            });
          }}
          indicator="dot"
        />
      }
    />
  );
}
